import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowUpRight,
    ArrowRight,
    ChevronDown,
    Sparkles,
} from "lucide-react";
import { api, fmtMoney } from "@/lib/api";
import { useEntity } from "@/lib/entityStore";
import { Card } from "@/components/ds/Card";
import { HealthCard } from "@/components/ds/HealthCard";
import { Heatmap } from "@/components/ds/Heatmap";
import { Loading, PageShell, Section } from "@/components/ds/PageShell";
import { SeverityDot, Confidence } from "@/components/ds/Severity";
import { DemoDataBanner } from "@/components/ds/DemoDataBanner";

const INITIAL_WMM = 3;

/* ---------- Helpers (deterministic, no AI) ---------- */

const safeDiv = (a, b) => (b ? a / b : 0);

function monthsOfCover(snapshot) {
    const cash = snapshot.total_cash ?? snapshot.cash_position ?? 0;
    const monthlyExpenses = snapshot.total_expenses_mtd ?? snapshot.expenses_mtd ?? 0;
    if (!monthlyExpenses) return null;
    return cash / monthlyExpenses;
}

function overdueArPct(snapshot) {
    const buckets = snapshot.ar_buckets || {};
    const total = snapshot.total_ar ?? snapshot.ar_total ?? Object.values(buckets).reduce((a, b) => a + b, 0);
    if (!total) return null;
    const overdue =
        (buckets["30"] || buckets["30+"] || 0) +
        (buckets["60"] || buckets["60+"] || 0) +
        (buckets["90"] || buckets["90+"] || 0);
    return (overdue / total) * 100;
}

function riskLabel(score) {
    if (score == null) return { label: "—", tone: "default" };
    if (score >= 60) return { label: "High", tone: "negative" };
    if (score >= 40) return { label: "Elevated", tone: "negative" };
    if (score >= 20) return { label: "Watch", tone: "warning" };
    return { label: "Low", tone: "positive" };
}

/* ------------ Heatmap derivation ------------ */

function levelFromScore(score) {
    if (score == null) return "none";
    if (score >= 90) return "ok";
    if (score >= 75) return "low";
    if (score >= 60) return "medium";
    if (score >= 40) return "high";
    return "critical";
}

function levelFromMargin(pct) {
    if (pct == null) return "none";
    if (pct >= 25) return "ok";
    if (pct >= 15) return "low";
    if (pct >= 5) return "medium";
    if (pct >= 0) return "high";
    return "critical";
}

function cashPressureLevel(snapshot) {
    const cash = snapshot.cash_position ?? 0;
    const monthlyExpenses = (snapshot.expenses_mtd ?? 0) || 1;
    const months = cash / monthlyExpenses;
    if (months >= 3) return { value: months, level: "ok" };
    if (months >= 2) return { value: months, level: "low" };
    if (months >= 1) return { value: months, level: "medium" };
    if (months >= 0.5) return { value: months, level: "high" };
    return { value: months, level: "critical" };
}

function overdueLevel(pct) {
    if (pct == null) return "none";
    if (pct <= 5) return "ok";
    if (pct <= 15) return "low";
    if (pct <= 30) return "medium";
    if (pct <= 50) return "high";
    return "critical";
}

function forecastSignal(snapshot) {
    const cash = snapshot.cash_position ?? 0;
    const np = snapshot.net_profit_mtd ?? 0;
    const exp = snapshot.expenses_mtd ?? 0;
    const rev = snapshot.revenue_mtd ?? 0;
    if (cash <= 0) return { label: "Critical", level: "critical" };
    if (np < 0 && cash < exp) return { label: "Tight", level: "high" };
    if (np < 0) return { label: "Soft", level: "medium" };
    if (np < rev * 0.05) return { label: "Thin", level: "low" };
    return { label: "Stable", level: "ok" };
}

/* ---------- Components ---------- */

// Calm one-line operator brief — the deterministic "AI brief of the day".
// Rendered between the intelligence strip and the Morning Brief Hero. Italic,
// subtle, visually secondary to the hero. Disappears when `brief` is null.
function DailyBriefLine({ brief, tone = "info", confidenceSoftened }) {
    if (!brief) return null;
    const dotCls = {
        critical: "bg-red-500",
        warning: "bg-amber-500",
        info: "bg-stone-400",
    }[tone] || "bg-stone-400";
    return (
        <div
            className="flex items-start gap-2.5 text-[13px] text-stone-600 italic leading-relaxed mb-5 max-w-prose"
            data-testid="overview-daily-brief"
            data-tone={tone}
            data-softened={confidenceSoftened ? "true" : "false"}
        >
            <span className={`w-1 h-1 rounded-full mt-2 flex-shrink-0 ${dotCls}`} />
            <span>{brief}</span>
        </div>
    );
}

// Calm one-line intelligence strip — "Updated · entities · attention · confidence".
function IntelligenceStrip({ as_of, entityCount, wmmCount, entitiesNeedingAction, confidence, isDemo }) {
    const parts = [];
    if (as_of) {
        const ageMin = Math.max(0, Math.round((Date.now() - new Date(as_of).getTime()) / 60000));
        if (ageMin < 1) parts.push("Updated just now");
        else if (ageMin < 60) parts.push(`Updated ${ageMin} min ago`);
        else parts.push(`Updated ${Math.round(ageMin / 60)}h ago`);
    }
    if (entityCount) parts.push(`Based on ${entityCount} ${entityCount === 1 ? "entity" : "entities"}`);
    if (wmmCount > 0) parts.push(`${wmmCount} ${wmmCount === 1 ? "issue needs" : "issues need"} attention`);
    if (entitiesNeedingAction > 0)
        parts.push(`${entitiesNeedingAction} ${entitiesNeedingAction === 1 ? "entity requires" : "entities require"} action`);
    if (confidence)
        parts.push(`Confidence: ${confidence}${isDemo ? " — demo data" : ""}`);
    if (parts.length === 0) return null;
    return (
        <div
            className="text-[12.5px] text-stone-500 leading-relaxed mb-4"
            data-testid="overview-intel-strip"
        >
            {parts.join(" · ")}
        </div>
    );
}

function PulseStat({ label, value, tone = "default", testId }) {
    const toneCls = {
        default: "text-stone-900",
        positive: "text-emerald-700",
        warning: "text-amber-700",
        negative: "text-red-700",
    }[tone] || "text-stone-900";
    return (
        <div data-testid={testId} className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-medium mb-1">
                {label}
            </div>
            <div className={`font-mono-num text-[20px] leading-tight font-medium tracking-tight ${toneCls}`}>
                {value}
            </div>
        </div>
    );
}

function MorningBriefHero({ wmm, truth, snapshot, navigate }) {
    const top = wmm?.items?.[0];
    const itemCount = wmm?.item_count ?? 0;

    const cashMonths = monthsOfCover(snapshot);
    const cashTone = cashMonths == null
        ? "default"
        : cashMonths >= 3 ? "positive"
            : cashMonths >= 1 ? "warning" : "negative";

    const np = snapshot.total_net_profit_mtd ?? 0;
    const profitTone = np > 0 ? "positive" : np < 0 ? "negative" : "warning";

    const overdue = overdueArPct(snapshot);
    const overdueTone = overdue == null ? "default"
        : overdue <= 15 ? "positive"
            : overdue <= 30 ? "warning" : "negative";

    const risk = riskLabel(snapshot.risk_score);

    const headline = !top
        ? "All clear today."
        : itemCount === 1
            ? "1 thing needs attention today."
            : `${itemCount} items above the attention threshold today.`;
    const focus = top
        ? `Focus first: ${top.entity_name} — ${top.title.toLowerCase()}.`
        : "Group health is within deterministic thresholds.";

    return (
        <Card
            variant="default"
            padding="lg"
            className="mb-6"
            testId="overview-hero"
        >
            <div className="flex items-center gap-2 text-stone-500 text-[12px] mb-3">
                <Sparkles size={13} strokeWidth={2} className="text-[var(--brand)]" />
                <span className="font-medium">Morning briefing</span>
                {truth?.overall_confidence && (
                    <>
                        <span>·</span>
                        <Confidence level={truth.overall_confidence} />
                    </>
                )}
            </div>
            <div
                className="text-display-md leading-snug text-stone-900 mb-2"
                data-testid="hero-headline"
            >
                {headline}
            </div>
            <p className="text-body text-stone-500 max-w-prose mb-6" data-testid="hero-focus">
                {focus}
            </p>

            {/* Pulse strip — the 4 numbers the owner must see in 5 seconds */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-5 pb-1">
                <PulseStat
                    label="Cash on hand"
                    value={
                        cashMonths == null
                            ? fmtMoney(snapshot.total_cash)
                            : `${fmtMoney(snapshot.total_cash)} · ${cashMonths.toFixed(1)} mo cover`
                    }
                    tone={cashTone}
                    testId="pulse-cash"
                />
                <PulseStat
                    label="Profit MTD"
                    value={fmtMoney(np)}
                    tone={profitTone}
                    testId="pulse-profit"
                />
                <PulseStat
                    label="Overdue AR"
                    value={overdue == null ? "—" : `${overdue.toFixed(0)}%`}
                    tone={overdueTone}
                    testId="pulse-overdue"
                />
                <PulseStat
                    label="Risk"
                    value={risk.label}
                    tone={risk.tone}
                    testId="pulse-risk"
                />
            </div>

            {top && (
                <div className="mt-6 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => navigate(top.drill_to || "/")}
                        className="inline-flex items-center gap-1.5 text-[13px] px-3 py-1.5 rounded-md text-white"
                        style={{ background: "var(--brand)" }}
                        data-testid="hero-top-action"
                    >
                        Open top priority
                        <ArrowUpRight size={13} strokeWidth={2} />
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/ai-expert")}
                        className="inline-flex items-center gap-1.5 text-[13px] px-3 py-1.5 rounded-md text-stone-700 bg-stone-100 hover:bg-stone-200"
                        data-testid="hero-ask-ai"
                    >
                        Ask the AI
                        <ArrowRight size={13} strokeWidth={2} />
                    </button>
                </div>
            )}
        </Card>
    );
}

function WhatMattersList({ wmm, navigate, expanded, onExpand }) {
    const items = useMemo(
        () => (expanded ? wmm.items : (wmm.items || []).slice(0, INITIAL_WMM)),
        [wmm, expanded],
    );
    if (!wmm || (wmm.items || []).length === 0) {
        return (
            <Card variant="default" padding="md">
                <div className="text-[14px] text-stone-700">
                    All clear — nothing ranks above the attention threshold right now.
                </div>
            </Card>
        );
    }
    return (
        <div className="space-y-2" data-testid="overview-wmm">
            {items.map((it, idx) => (
                <button
                    key={it.id}
                    type="button"
                    onClick={() => navigate(it.drill_to || "/")}
                    className="w-full text-left rounded-lg border border-stone-100 bg-white hover:border-stone-200 transition px-5 py-4 group"
                    data-testid={`overview-wmm-item-${idx}`}
                >
                    <div className="flex items-start gap-4">
                        <div className="pt-1.5">
                            <SeverityDot level={it.severity} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 mb-1">
                                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-medium">
                                    {it.entity_name}
                                </div>
                                <span className="text-stone-300">·</span>
                                <Confidence level={it.confidence || "medium"} />
                            </div>
                            <div className="text-[15px] font-medium text-stone-900 leading-snug">
                                {it.title}
                            </div>
                            <div className="text-[13px] text-stone-600 mt-1 leading-relaxed">
                                {it.why_it_matters}
                            </div>
                            <div className="text-[13px] text-stone-500 mt-1.5">
                                <span className="text-stone-700 font-medium">
                                    Recommended:
                                </span>{" "}
                                {it.recommended_action}
                            </div>
                        </div>
                        <ArrowUpRight
                            size={14}
                            strokeWidth={2}
                            className="text-stone-300 group-hover:text-stone-600 transition mt-1 flex-shrink-0"
                        />
                    </div>
                </button>
            ))}
            {(wmm.items || []).length > INITIAL_WMM && (
                <button
                    type="button"
                    onClick={onExpand}
                    className="text-[13px] text-stone-600 hover:text-stone-900 inline-flex items-center gap-1.5 mt-2"
                    data-testid="overview-wmm-expand"
                >
                    <ChevronDown
                        size={12}
                        strokeWidth={2}
                        className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                    />
                    {expanded
                        ? "Show top 3 only"
                        : `View all ${(wmm.items || []).length}`}
                </button>
            )}
        </div>
    );
}

export default function Overview() {
    const { selectedEntityId, isGroup } = useEntity();
    const nav = useNavigate();
    const [group, setGroup] = useState(null);
    const [wmm, setWmm] = useState(null);
    const [truth, setTruth] = useState(null);
    const [recon, setRecon] = useState(null);
    const [dailyBrief, setDailyBrief] = useState(null);
    const [loading, setLoading] = useState(true);
    const [wmmExpanded, setWmmExpanded] = useState(false);

    useEffect(() => {
        if (!isGroup) {
            nav(`/entities/${selectedEntityId}`, { replace: true });
            return;
        }
        (async () => {
            setLoading(true);
            try {
                const [g, w, t, r, db] = await Promise.all([
                    api.get("/group/dashboard"),
                    api.get("/what-matters?limit=10"),
                    api.get("/truth/group"),
                    api.get("/reconciliation"),
                    api.get("/overview/daily-brief").catch(() => ({ data: null })),
                ]);
                setGroup(g.data);
                setWmm(w.data);
                setTruth(t.data);
                setRecon(r.data);
                setDailyBrief(db?.data || null);
            } finally {
                setLoading(false);
            }
        })();
    }, [selectedEntityId, isGroup, nav]);

    const heatmapData = useMemo(() => {
        if (!group?.snapshot?.per_entity) return { entities: [], data: {} };
        const reconByEntity = Object.fromEntries(
            (recon?.per_entity || []).map((p) => [p.entity_id, p.summary]),
        );
        const entities = group.snapshot.per_entity.map((p) => ({
            id: p.entity_id,
            name: p.display_name,
        }));
        const data = {};
        for (const p of group.snapshot.per_entity) {
            const s = p.snapshot;
            const cash = cashPressureLevel(s);
            const margin = s.gross_margin_mtd_pct;
            const overdue = overdueArPct(s);
            const reconScore = reconByEntity[p.entity_id]?.reconciliation_score;
            const fr = forecastSignal(s);
            data[p.entity_id] = {
                cash: {
                    value: `${cash.value.toFixed(1)} mo`,
                    level: cash.level,
                },
                profit: {
                    value: margin != null ? `${margin.toFixed(0)}%` : "—",
                    level: levelFromMargin(margin),
                },
                overdue: {
                    value: overdue == null ? "—" : `${overdue.toFixed(0)}%`,
                    level: overdueLevel(overdue),
                },
                recon: {
                    value: reconScore == null ? "—" : Math.round(reconScore).toString(),
                    level: levelFromScore(reconScore),
                },
                forecast: { value: fr.label, level: fr.level },
            };
        }
        return { entities, data };
    }, [group, recon]);

    if (loading || !group) {
        return (
            <PageShell width="default">
                <Loading
                    label="Loading the command centre…"
                    testId="overview-loading"
                />
            </PageShell>
        );
    }

    const s = group.snapshot;
    const isMock = (truth?.data_mode || s.data_mode) !== "production_live";
    const overdue = overdueArPct(s);
    const months = monthsOfCover(s);
    const risk = riskLabel(s.risk_score);

    // Intelligence-strip metadata (deterministic, all derivable client-side).
    const wmmCount = wmm?.item_count ?? (wmm?.items?.length || 0);
    const entitiesNeedingAction = (group?.snapshot?.per_entity || [])
        .filter((p) => {
            const sn = p.snapshot || {};
            const np = sn.net_profit_mtd ?? 0;
            const cp = cashPressureLevel(sn);
            const od = overdueArPct(sn);
            return np < 0 || cp.level === "critical" || cp.level === "high" || (od != null && od > 30);
        }).length;
    const intelConfidence = (truth?.overall_confidence || "—").replace(/^\w/, (c) => c.toUpperCase());

    // Heatmap takeaway — one calm line above the chart.
    const heatmapTakeaway = (() => {
        const ents = heatmapData?.entities || [];
        const data = heatmapData?.data || {};
        if (ents.length === 0) return null;
        const critical = ents.filter((e) => {
            const row = data[e.id] || {};
            return Object.values(row).some((c) => c.level === "critical");
        });
        const ok = ents.filter((e) => {
            const row = data[e.id] || {};
            return Object.values(row).every((c) => ["ok", "low"].includes(c.level));
        });
        if (critical.length > 0) {
            const names = critical.slice(0, 2).map((e) => e.name).join(" and ");
            return `${names} ${critical.length === 1 ? "is" : "are"} carrying critical pressure — open the entity to see which dimension.`;
        }
        if (ok.length === ents.length) {
            return "All entities are within deterministic thresholds today.";
        }
        return `${ok.length} of ${ents.length} entities are healthy across all five signals; the rest need a closer look.`;
    })();

    return (
        <PageShell width="default" testId="overview-page">
            {isMock && (
                <div className="mb-3">
                    <DemoDataBanner />
                </div>
            )}

            <IntelligenceStrip
                as_of={truth?.as_of || s?.as_of}
                entityCount={s.entity_count}
                wmmCount={wmmCount}
                entitiesNeedingAction={entitiesNeedingAction}
                confidence={intelConfidence}
                isDemo={isMock}
            />

            <DailyBriefLine
                brief={dailyBrief?.brief}
                tone={dailyBrief?.tone}
                confidenceSoftened={dailyBrief?.confidence_softened}
            />

            <MorningBriefHero
                wmm={wmm}
                truth={truth}
                snapshot={s}
                navigate={nav}
            />

            <Section
                title="What matters most"
                subtitle="Deterministic — ranked from forecast stress, audits, reconciliation, patterns and notes."
            >
                <WhatMattersList
                    wmm={wmm}
                    navigate={nav}
                    expanded={wmmExpanded}
                    onExpand={() => setWmmExpanded((v) => !v)}
                />
            </Section>

            <Section
                title="Group health"
                subtitle="Five calm signals across the group."
            >
                <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3"
                    data-testid="group-health"
                >
                    <HealthCard
                        label="Cash on hand"
                        value={s.total_cash}
                        interpretation={
                            months == null
                                ? `Across ${s.entity_count} ${s.entity_count === 1 ? "entity" : "entities"}`
                                : `${months.toFixed(1)} months of expenses cover`
                        }
                        onClick={() => nav("/cashflow")}
                        testId="health-cash"
                    />
                    <HealthCard
                        label="Net profit MTD"
                        value={s.total_net_profit_mtd}
                        tone={s.total_net_profit_mtd >= 0 ? "positive" : "negative"}
                        interpretation={`Revenue MTD ${fmtMoney(s.total_revenue_mtd)}`}
                        onClick={() => nav("/group")}
                        testId="health-profit"
                    />
                    <HealthCard
                        label="Receivables"
                        value={s.total_ar}
                        tone={overdue && overdue > 30 ? "warning" : "default"}
                        interpretation={
                            overdue != null
                                ? `${overdue.toFixed(0)}% overdue (30d+)`
                                : `Across ${s.entity_count} ${s.entity_count === 1 ? "entity" : "entities"}`
                        }
                        onClick={() => nav("/debtors")}
                        testId="health-receivables"
                    />
                    <HealthCard
                        label="Risk score"
                        value={s.risk_score ?? "—"}
                        money={false}
                        tone={risk.tone}
                        interpretation={
                            risk.label === "—"
                                ? "Awaiting deterministic findings"
                                : `${risk.label} — based on open audit findings`
                        }
                        onClick={() => nav("/audit")}
                        testId="health-risk"
                    />
                    <HealthCard
                        label="Data confidence"
                        value={(truth?.overall_confidence || "—").toUpperCase()}
                        money={false}
                        confidence={truth?.overall_confidence}
                        tone={
                            truth?.overall_confidence === "high"
                                ? "positive"
                                : truth?.overall_confidence === "low"
                                    ? "negative"
                                    : "warning"
                        }
                        interpretation={
                            (truth?.sources_missing_aggregate || []).length > 0
                                ? `Missing: ${(truth.sources_missing_aggregate || []).slice(0, 2).join(", ")}`
                                : "All deterministic sources present"
                        }
                        onClick={() => nav("/production-readiness")}
                        testId="health-confidence"
                    />
                </div>
            </Section>

            <Section
                title="Entity heatmap"
                subtitle="Where the risk sits today, across the six companies."
            >
                {heatmapTakeaway && (
                    <div
                        className="text-[13px] text-stone-600 leading-relaxed mb-3 max-w-prose"
                        data-testid="heatmap-takeaway"
                    >
                        {heatmapTakeaway}
                    </div>
                )}
                <Heatmap
                    entities={heatmapData.entities}
                    dimensions={[
                        { key: "cash", label: "Cash cover" },
                        { key: "profit", label: "Margin" },
                        { key: "overdue", label: "Overdue AR" },
                        { key: "recon", label: "Recon score" },
                        { key: "forecast", label: "Forecast" },
                    ]}
                    data={heatmapData.data}
                    onCellClick={(entity) => nav(`/entities/${entity.id}`)}
                    testId="entity-heatmap"
                />
            </Section>
        </PageShell>
    );
}
